import { CREW_SEATS, CREW_TAGGER_SEAT_KEYS, crewSeatName } from "./crewCode";

/** Seat columns, in the order the tagger writes them. */
export const CREW_HOUR_SEATS = [
  { key: "Helmsman", label: "Helmsman", short: "Helm" },
  { key: "Wing Trim", label: "Wing Trim", short: "Wing" },
  { key: "Flight Ctrl", label: "Flight Ctrl", short: "Flight" },
  { key: "G1", label: "G1", short: "G1" },
  { key: "G2", label: "G2", short: "G2" },
  { key: "Strategy", label: "Strategy", short: "Strat" },
] as const;

export type CrewHourSeatKey = (typeof CREW_HOUR_SEATS)[number]["key"];
export type CrewHourMode = "all" | "racing" | "training";

/** Seat names for the hours table. Reads the canonical key, then a legacy form label. */
export function crewHourSeatsFromTags(tags: Record<string, unknown> | null | undefined): Partial<Record<CrewHourSeatKey, string>> {
  const seats: Partial<Record<CrewHourSeatKey, string>> = {};
  if (!tags) {
    return seats;
  }
  for (const seat of CREW_SEATS) {
    const name = crewSeatName(tags, seat);
    if (!name) {
      continue;
    }
    const key = CREW_TAGGER_SEAT_KEYS[seat] as CrewHourSeatKey;
    seats[key] = name;
  }
  return seats;
}

export interface CrewHourEvent {
  date: string;
  startMs: number;
  endMs: number;
  sources: string[];
  crewCode: string;
  raceNumber: number | null;
  seats: Partial<Record<CrewHourSeatKey, string>>;
}

export interface CrewHourSailorRow {
  sailor: string;
  hours: Record<CrewHourSeatKey, number>;
  total: number;
}

export interface CrewHourCrewRow {
  code: string;
  hours: number;
  intervals: number;
}

export interface CrewHourSummary {
  rows: CrewHourSailorRow[];
  seatTotals: Record<CrewHourSeatKey, number>;
  wallHours: number;
  intervalCount: number;
  byCrew: CrewHourCrewRow[];
  /** Intervals with no race number. Counted in All, omitted from Racing and Training. */
  unnumbered: number;
}

export interface CrewHourFilter {
  sourceName: string;
  startDate: string;
  endDate: string;
  mode: CrewHourMode;
}

const EMPTY_SEAT = "NA";

export function crewEventHours(event: CrewHourEvent): number {
  if (!Number.isFinite(event.startMs) || !Number.isFinite(event.endMs) || event.endMs <= event.startMs) {
    return 0;
  }
  return (event.endMs - event.startMs) / 3_600_000;
}

function blankSeatHours(): Record<CrewHourSeatKey, number> {
  return {
    Helmsman: 0,
    "Wing Trim": 0,
    "Flight Ctrl": 0,
    G1: 0,
    G2: 0,
    Strategy: 0,
  };
}

function sailorLabel(raw: string | undefined): string {
  const name = (raw ?? "").trim();
  if (!name || name.toUpperCase() === EMPTY_SEAT) return "";
  return name;
}

export function crewEventMatches(event: CrewHourEvent, filter: CrewHourFilter): boolean {
  const source = filter.sourceName.trim().toLowerCase();
  if (!source) return false;
  if (!event.sources.some((name) => name.trim().toLowerCase() === source)) return false;
  if (!event.crewCode.trim()) return false;
  if (crewEventHours(event) <= 0) return false;

  const start = filter.startDate.trim();
  const end = filter.endDate.trim();
  if ((start || end) && !event.date) return false;
  if (start && event.date < start) return false;
  if (end && event.date > end) return false;

  if (filter.mode === "racing") return event.raceNumber != null && event.raceNumber > 0;
  if (filter.mode === "training") return event.raceNumber === -1;
  return true;
}

export function summarizeCrewHours(events: readonly CrewHourEvent[], filter: CrewHourFilter): CrewHourSummary {
  const seatTotals = blankSeatHours();
  const bySailor = new Map<string, Record<CrewHourSeatKey, number>>();
  const byCrew = new Map<string, { hours: number; intervals: number }>();
  let wallHours = 0;
  let intervalCount = 0;
  const inRange = events.filter((event) => crewEventMatches(event, { ...filter, mode: "all" }));
  const unnumbered = inRange.filter((event) => event.raceNumber == null).length;

  for (const event of events) {
    if (!crewEventMatches(event, filter)) continue;
    const hours = crewEventHours(event);
    wallHours += hours;
    intervalCount += 1;

    const code = event.crewCode.trim();
    const crew = byCrew.get(code) ?? { hours: 0, intervals: 0 };
    crew.hours += hours;
    crew.intervals += 1;
    byCrew.set(code, crew);

    for (const seat of CREW_HOUR_SEATS) {
      const sailor = sailorLabel(event.seats[seat.key]);
      if (!sailor) continue;
      const row = bySailor.get(sailor) ?? blankSeatHours();
      row[seat.key] += hours;
      bySailor.set(sailor, row);
      seatTotals[seat.key] += hours;
    }
  }

  const rows: CrewHourSailorRow[] = [...bySailor.entries()]
    .map(([sailor, hours]) => ({
      sailor,
      hours,
      total: CREW_HOUR_SEATS.reduce((sum, seat) => sum + hours[seat.key], 0),
    }))
    .sort((a, b) => b.total - a.total || a.sailor.localeCompare(b.sailor));

  const crewRows: CrewHourCrewRow[] = [...byCrew.entries()]
    .map(([code, value]) => ({ code, hours: value.hours, intervals: value.intervals }))
    .sort((a, b) => b.hours - a.hours || a.code.localeCompare(b.code));

  return {
    rows,
    seatTotals,
    wallHours,
    intervalCount,
    byCrew: crewRows,
    unnumbered,
  };
}
